import { useTranslation } from "react-i18next";
import { formatFullDate, formatRelativeTime } from "@/lib/format";
import type { EnforcementMode, PolicyChange } from "@/pages/admin/apikeys/hooks/useApiKeys";

interface ApiPolicyHistoryProps {
  history: PolicyChange[];
  loading: boolean;
}

function modeOf(value: string | undefined): EnforcementMode | null {
  if (value === "required" || value === "optional") return value;
  return null;
}

// 策略变更历史：回看每一次「强制 Key ↔ 过渡兼容」的调整，含操作人 / 时间与前后方向，
// 便于治理误切与回溯线上客户端受影响的时间点。
export default function ApiPolicyHistory({ history, loading }: ApiPolicyHistoryProps) {
  const { t } = useTranslation();
  const labelOf = (mode: EnforcementMode) =>
    mode === "required" ? t("apikeys.policyRequired") : t("apikeys.policyOptional");

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
          <i className="ri-history-line" />
        </span>
        <div>
          <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("apikeys.policyHistoryTitle")}</h2>
          <p className="text-[11px] text-foreground-500">{t("apikeys.policyHistorySub")}</p>
        </div>
      </div>

      {loading ? (
        <div className="mt-4 flex flex-col gap-2">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-11 rounded-md bg-background-100" />
          ))}
        </div>
      ) : history.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 py-8 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-history-line text-lg" />
          </span>
          <p className="text-sm font-medium text-foreground-800">{t("apikeys.policyHistoryEmpty")}</p>
          <p className="text-xs text-foreground-500">{t("apikeys.policyHistoryEmptyDesc")}</p>
        </div>
      ) : (
        <ul className="mt-4 flex flex-col gap-2">
          {history.map((item) => {
            const from = modeOf(item.meta?.from);
            const to = modeOf(item.meta?.mode);
            return (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
                      to === "optional" ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-700"
                    }`}
                  >
                    <i className={to === "optional" ? "ri-lock-unlock-line" : "ri-lock-2-line"} />
                  </span>
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-900">
                      {from && from !== to ? (
                        <>
                          <span className="text-foreground-500">{labelOf(from)}</span>
                          <span className="flex h-3 w-3 items-center justify-center text-foreground-400">
                            <i className="ri-arrow-right-line" />
                          </span>
                          <span className={to === "optional" ? "font-medium text-accent-800" : "font-medium text-primary-700"}>
                            {to ? labelOf(to) : item.detail}
                          </span>
                        </>
                      ) : (
                        <span className="text-foreground-800">{item.detail ?? item.action}</span>
                      )}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] text-foreground-500">
                      {item.actor_name ?? t("apikeys.unknown")}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-foreground-600">{formatFullDate(item.created_at)}</p>
                  <p className="text-[10px] text-foreground-400">{formatRelativeTime(item.created_at)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}